import { captureException, startSpan } from "@sentry/react";

import {
    IGenerateImageResponse,
    IPollGenerateImage,
    PromptGeneratorInput,
} from "../../types";
import { doGet, doPost } from "../core/http";

export const mergeAudioAndVideo = async (
    formData: FormData
): Promise<IGenerateImageResponse> => {
    const url = "/api/v1/video/merge";

    try {
        const { data, error } = await startSpan(
            { name: "mergeAudioAndVideo" },
            () => {
                return doPost(url, true, formData);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { requestId: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to create image." }; // Consider more specific error
    }
};

export const generateVideo = async (
    formData: FormData
): Promise<IGenerateImageResponse> => {
    const url = "/api/v1/video/generate";

    try {
        const { data, error } = await startSpan(
            { name: "generateVideo" },
            () => {
                return doPost(url, true, formData);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { requestId: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to create image." }; // Consider more specific error
    }
};

export const generateAudio = async (
    formData: FormData
): Promise<IGenerateImageResponse> => {
    const url = "/api/v1/audio/generate";

    try {
        const { data, error } = await startSpan(
            { name: "generateAudio" },
            () => {
                return doPost(url, true, formData);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { requestId: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to create image." }; // Consider more specific error
    }
};

export const pollGenerateVideo = async (
    requestId: string
): Promise<IPollGenerateImage> => {
    try {
        const image = `/api/v1/video/generate/${requestId}/poll`;

        const { data, error } = await startSpan(
            { name: "pollGenerateVideo" },
            () => {
                return doGet(image, true);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return {
            image: data,
        };
    } catch (e) {
        console.error(e); // Keep console.error if original used it
        return { error: "Failed to get image." };
    }
};

export const pollGenerateAudio = async (
    requestId: string
): Promise<IPollGenerateImage> => {
    try {
        const image = `/api/v1/audio/generate/${requestId}/poll`;

        const { data, error } = await startSpan(
            { name: "pollGenerateAudio" },
            () => {
                return doGet(image, true);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return {
            image: data,
        };
    } catch (e) {
        console.error(e); // Keep console.error if original used it
        return { error: "Failed to get image." };
    }
};

export const generateEnhancedPrompt = async (
    promptData: PromptGeneratorInput
): Promise<{ prompt?: string; error?: string }> => {
    try {
        const { data, error } = await startSpan(
            { name: "generateEnhancedPrompt" },
            async () => {
                const formData = new FormData();
                formData.append("prompt", promptData.input_concept);
                formData.append("style", promptData.style || "Simple");
                formData.append(
                    "cameraStyle",
                    promptData.camera_style || "None"
                );
                formData.append(
                    "cameraDirection",
                    promptData.camera_direction || "None"
                );
                formData.append("pacing", promptData.pacing || "None");
                formData.append(
                    "specialEffects",
                    promptData.special_effects || "None"
                );
                formData.append(
                    "customElements",
                    promptData.custom_elements || ""
                );
                formData.append(
                    "model",
                    promptData.model || "google/gemini-flash-1.5"
                );
                formData.append(
                    "promptLength",
                    promptData.prompt_length || "Medium"
                );

                return await doPost(
                    "/api/v1/video/prompt/generate",
                    true,
                    formData
                );
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { prompt: data || data }; // Assuming backend returns { prompt: "..." }
    } catch (e) {
        captureException(e);
        return { error: "Failed to generate enhanced prompt." };
    }
};
